import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileDown, Mail, Phone, UserPlus } from "lucide-react";
import { api, errMsg, download } from "@/lib/api";
import { PageHeader, Empty } from "@/components/Common";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { ActivateDialog } from "@/components/ActivateDialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATI = { nuova: "Nuova", contattata: "Contattata", attivata: "Attivata", scartata: "Scartata" };
const TONE = { nuova: "border-l-sky-500", contattata: "border-l-amber-500", attivata: "border-l-emerald-500", scartata: "border-l-slate-300" };

export default function Requests() {
  const [items, setItems] = useState(null);
  const [act, setAct] = useState(null);
  const load = useCallback(() => api.get("/admin/requests").then((r) => setItems(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    try { await api.put(`/admin/requests/${id}`, { status }); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div>
      <PageHeader title="Richieste di account" subtitle="Studi e aziende che hanno compilato il modulo sulla homepage" />
      {items && items.length === 0 ? <Empty text="Nessuna richiesta ricevuta" /> : (
        <div className="space-y-3" data-testid="requests-list">
          {(items || []).map((r) => (
            <div key={r.id} data-testid={`request-row-${r.id}`} className={`rounded-xl border border-l-4 border-slate-200 bg-white p-5 ${TONE[r.status]}`}>
              <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="rounded bg-slate-100 px-2 py-0.5 text-xs font-semibold">{{ studio: "Studio", partner: "Partner", ricarica: "Ricarica pacchetto" }[r.tipo] || "Azienda"}</span>
                    <h3 className="font-display text-base font-bold text-slate-900">{r.denominazione}</h3>
                    {r.piva && <span className="font-mono text-xs text-slate-500">P.IVA {r.piva}</span>}
                  </div>
                  <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-sm text-slate-600">
                    <span>{r.nome}</span>
                    <a href={`mailto:${r.email}`} className="inline-flex items-center gap-1 text-sky-700 hover:underline"><Mail className="h-3.5 w-3.5" />{r.email}</a>
                    {r.telefono && <a href={`tel:${r.telefono}`} className="inline-flex items-center gap-1"><Phone className="h-3.5 w-3.5" />{r.telefono}</a>}
                  </div>
                  {r.package_name && <div data-testid={`request-package-${r.id}`} className="mt-2 text-sm font-semibold text-sky-700">Pacchetto richiesto: {r.package_name}</div>}
                  {r.codice_fiscale && (
                    <div data-testid={`request-fiscal-${r.id}`} className="mt-2 text-xs leading-relaxed text-slate-600">
                      C.F. {r.codice_fiscale}{r.regime_fiscale && ` · ${r.regime_fiscale}`} · {r.indirizzo}, {r.cap} {r.comune} ({r.provincia})
                      {r.pec && ` · PEC ${r.pec}`}{r.codice_destinatario && ` · Cod. dest. ${r.codice_destinatario}`}
                    </div>
                  )}
                  {r.messaggio && <p className="mt-3 whitespace-pre-line text-sm text-slate-600">{r.messaggio}</p>}
                  <div className="mt-2 text-xs text-slate-600">{new Date(r.created_at).toLocaleString("it-IT")} · {r.email_sent ? "notifica email inviata" : "notifica email non inviata"}</div>
                </div>
                <div className="flex items-center gap-2">
                  {r.package_id && (
                    <Button size="sm" variant="outline" data-testid={`request-modulo-pdf-${r.id}`} onClick={() => download(`/admin/requests/${r.id}/modulo.pdf`)}>
                      <FileDown className="mr-1.5 h-4 w-4" />Modulo PDF
                    </Button>
                  )}
                  {!r.created_ref && r.status !== "scartata" && r.tipo !== "ricarica" && (
                    <Button size="sm" data-testid={`activate-request-${r.id}`} className="bg-sky-600 hover:bg-sky-700" onClick={() => setAct(r)}>
                      <UserPlus className="mr-1.5 h-4 w-4" />Crea account
                    </Button>
                  )}
                  {r.created_ref && <span data-testid={`request-created-${r.id}`} className="rounded bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700">Account creato</span>}
                  <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                    <SelectTrigger data-testid={`request-status-${r.id}`} className="w-36"><SelectValue /></SelectTrigger>
                    <SelectContent>{Object.entries(STATI).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
                  </Select>
                  <ConfirmDelete testid={`delete-request-${r.id}`} onConfirm={async () => { await api.delete(`/admin/requests/${r.id}`); load(); }} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      <ActivateDialog req={act} onClose={() => setAct(null)} onDone={() => { setAct(null); load(); }} />
      <p className="mt-6 text-xs text-slate-500">Con “Crea account” nasce lo studio o l'azienda con il suo amministratore, che riceve un'email di benvenuto per scegliere la password.</p>
    </div>
  );
}
